/**
 * NX-2744 (E8.13) — the ADR-018 time-travel UI: as-of control, snapshot resolution, and the
 * knowledge banner.
 *
 * The structural guarantee AC3 exists for: when an as-of or snapshot view is active, the LIVE
 * screen never mounts. Project.tsx swaps this component in for its entire body, so no live panel
 * can be viewed, screenshotted or printed as if it were the past — and every block this component
 * renders carries the banner itself, not just the page header.
 *
 * Honesty about depth: the server's as-of reconstruction covers project IDENTITY and STATUS today
 * (AS_OF_PROJECT_POLICY: projectId/parent/scope/name/status). Analytics panels are NOT
 * reconstructable at a past moment yet, and rendering today's analytics under a snapshot banner
 * would be a lie — so the analytics section states exactly that, as an honest state, banner
 * attached. When the server learns to reconstruct more, this screen widens; it never fakes.
 *
 * AC5/AC6: the server's specific 400s (retention window naming the oldest reconstructable as_of;
 * ambiguous_as_of; malformed dates) surface verbatim — the user learns how far back they CAN go.
 */
import { useSearchParams } from "react-router-dom";
import { useApi } from "../api/useApi.js";
import { HonestState, Spine, spineLevels, type Rag } from "../components.js";
import { Button, DateTimeInput, Field, Input } from "../components/controls.js";
import { formatDate } from "../format.js";
import { humanizeReason } from "../format.js";

interface AsOfProject {
  readonly projectId: string;
  readonly parentProjectId: string | null;
  readonly scopeType: string;
  readonly name: string;
  readonly status: string;
}

const STATUS_RAG: Record<string, Rag> = {
  healthy: "green",
  green: "green",
  warning: "amber",
  yellow: "amber",
  bad: "red",
  red: "red",
};

/** The ADR-018 knowledge banner — rendered by EVERY block of the as-of screen (AC3), including
 *  loading and error states (round-1 review finding): before the server answers, the banner
 *  describes the REQUESTED moment, so not even a spinner or an error can pass for the present. */
export function KnowledgeBanner({ moment }: { moment: string }): React.ReactNode {
  return (
    <div className="knowbanner" role="status">
      Showing what SeneSight knew as of <b>{moment}</b> — not the current state.
    </div>
  );
}

/** The human descriptor of what was asked for — resolves to the server-confirmed timestamp once
 *  the response arrives, and to the request itself before/without one. */
function requestedMoment(asOfParam: string | null, snapshotParam: string | null): string {
  if (snapshotParam !== null) return `snapshot "${snapshotParam}"`;
  return asOfParam ?? "a past moment";
}

/** Reads the time-travel request from the URL. Exactly one of asOf/snapshot; both set is passed
 *  through so the SERVER's ambiguous_as_of contract answers (AC6) — the client never pre-empts it. */
export function useAsOfRequest(): {
  active: boolean;
  query: string;
  asOfParam: string | null;
  snapshotParam: string | null;
} {
  const [params] = useSearchParams();
  const asOfParam = params.get("as_of");
  const snapshotParam = params.get("snapshot");
  const parts: string[] = [];
  if (asOfParam !== null) parts.push(`as_of=${encodeURIComponent(asOfParam)}`);
  if (snapshotParam !== null) parts.push(`snapshot=${encodeURIComponent(snapshotParam)}`);
  return {
    active: parts.length > 0,
    query: parts.join("&"),
    asOfParam,
    snapshotParam,
  };
}

/** The as-of / snapshot control. Rendered on the project screen in BOTH modes; "Now" (AC4)
 *  clears the query state entirely — no lingering visual trace. */
export function AsOfControl(): React.ReactNode {
  const [params, setParams] = useSearchParams();
  const active = params.get("as_of") !== null || params.get("snapshot") !== null;
  return (
    <form
      className="asofctl"
      onSubmit={(e) => {
        e.preventDefault();
        const form = new FormData(e.currentTarget);
        const when = String(form.get("when") ?? "");
        const snapshot = String(form.get("snap") ?? "").trim();
        const next = new URLSearchParams();
        // One mechanism at a time: a named snapshot wins if given, else the date. Never both —
        // the server's ambiguous_as_of contract is for callers that construct URLs by hand.
        if (snapshot !== "") next.set("snapshot", snapshot);
        else if (when !== "") next.set("as_of", new Date(when).toISOString());
        setParams(next);
      }}
    >
      <Field label="View project as of date and time">
        <DateTimeInput name="when" />
      </Field>
      <Field label="View project at a named snapshot">
        <Input type="text" name="snap" placeholder="snapshot name" />
      </Field>
      <Button type="submit" size="sm">
        View
      </Button>
      {active && (
        <Button size="sm" variant="ghost" onClick={() => setParams(new URLSearchParams())}>
          Back to Now
        </Button>
      )}
    </form>
  );
}

/** The full past-view screen. Mounted INSTEAD of the live project body whenever time-travel is
 *  active — the structural impossibility AC3 asks for. */
export function AsOfProjectView({
  projectId,
  query,
  asOfParam,
  snapshotParam,
}: {
  projectId: string;
  query: string;
  asOfParam: string | null;
  snapshotParam: string | null;
}): React.ReactNode {
  const state = useApi<AsOfProject>(`/projects/${projectId}/as-of?${query}`);
  const requested = requestedMoment(asOfParam, snapshotParam);
  if (state.status === "loading") {
    return (
      <div className="panel pad enter">
        <KnowledgeBanner moment={requested} />
        <div className="meta">Reconstructing past state…</div>
      </div>
    );
  }
  if (state.status === "error") {
    // AC5/AC6 — the server's OWN words, VERBATIM (round-1 review finding: no humanizing here —
    // "as_of" is contract wording the user needs exactly as the server wrote it): the retention
    // window naming the oldest reconstructable moment, ambiguous_as_of, malformed dates.
    return (
      <div className="panel pad enter">
        <KnowledgeBanner moment={requested} />
        <HonestState
          kind="notobs"
          detail={state.error.message}
          action="Adjust the date, or return to Now."
        />
      </div>
    );
  }
  const p = state.envelope.data;
  const asOf = state.envelope.meta.asOf ?? new Date().toISOString();
  const levels = spineLevels(STATUS_RAG[p.status] ?? "neutral");
  return (
    <>
      <div className="panel pad enter">
        <KnowledgeBanner moment={formatDate(asOf)} />
        <div className="proghead">
          <Spine levels={levels} label={`Status then: ${p.status}`} />
          <h2>{p.name}</h2>
          <span className="kind">
            {p.scopeType} · status as of {formatDate(asOf)}: <b>{humanizeReason(p.status)}</b>
          </span>
        </div>
      </div>
      <div className="panel pad enter">
        <KnowledgeBanner moment={formatDate(asOf)} />
        <HonestState
          kind="notobs"
          title="Delivery analytics"
          detail="Not reconstructable for a past moment yet — as-of reconstruction covers project identity and status today. What you see above is what SeneSight knew then; today's analytics are deliberately NOT shown here, because they describe now, not this moment."
        />
      </div>
    </>
  );
}
